/** @jsx jsx */
import { useContext, useState, useEffect } from 'react';
import { Box, Flex, Heading, Text, Button, jsx, Label, Alert, Textarea, Grid } from 'theme-ui';
import { IoIosClose } from 'react-icons/io';
import { CartContext } from '../../provider/cart-provider';
import styles from './cart.style';
import { LocalCartContext } from '../../provider/local-cart-provider';
import WebCheckout from './webCheckout'
import AuthStateApp from '../Authenticator/AuthStateApp'
import awsconfig from '../../aws-exports';
import Amplify from 'aws-amplify';

Amplify.configure(awsconfig);

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { API } from 'aws-amplify'
const holidayDates = [ 
new Date('2026-12-25'),
new Date('2026-12-26')
]

const includeDates = [new Date('2025-11-08')]

const pointOnSegment = (longitude: number, latitude: number, first: number[], second: number[]) => {
	const cross = (longitude - first[0]) * (second[1] - first[1]) - (latitude - first[1]) * (second[0] - first[0]);
	if (Math.abs(cross) > 1e-10) return false;
	return longitude >= Math.min(first[0], second[0]) - 1e-10 && longitude <= Math.max(first[0], second[0]) + 1e-10 &&
		latitude >= Math.min(first[1], second[1]) - 1e-10 && latitude <= Math.max(first[1], second[1]) + 1e-10;
};

const pointInRing = (longitude: number, latitude: number, ring: number[][]) => {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const first = ring[i];
		const second = ring[j];
		if (pointOnSegment(longitude, latitude, first, second)) return true;
		const crossesLatitude = (first[1] > latitude) !== (second[1] > latitude);
		if (crossesLatitude && longitude < (second[0] - first[0]) * (latitude - first[1]) / (second[1] - first[1]) + first[0]) {
			inside = !inside;
		}
	}
	return inside;
};

const pointInPolygon = (longitude: number, latitude: number, polygon: number[][][]) => {
	if (!polygon.length || !pointInRing(longitude, latitude, polygon[0])) return false;
	return !polygon.slice(1).some(hole => pointInRing(longitude, latitude, hole));
};

const findZonesAtPoint = (deliveryZones: any, longitude: number, latitude: number): any[] => deliveryZones.features.filter((feature: any) => {
	const geometry = feature.geometry;
	if (!geometry) return false;
	if (geometry.type === 'Polygon') return pointInPolygon(longitude, latitude, geometry.coordinates);
	if (geometry.type === 'MultiPolygon') return geometry.coordinates.some((polygon: number[][][]) => pointInPolygon(longitude, latitude, polygon));
	return false;
});

const getDistanceToSegmentMeters = (longitude: number, latitude: number, first: number[], second: number[]) => {
	const metersPerDegreeLongitude = 111320 * Math.cos(latitude * Math.PI / 180);
	const firstX = (first[0] - longitude) * metersPerDegreeLongitude;
	const firstY = (first[1] - latitude) * 110540;
	const secondX = (second[0] - longitude) * metersPerDegreeLongitude;
	const secondY = (second[1] - latitude) * 110540;
	const segmentX = secondX - firstX;
	const segmentY = secondY - firstY;
	const lengthSquared = segmentX * segmentX + segmentY * segmentY;
	const projection = lengthSquared ? Math.max(0, Math.min(1, -(firstX * segmentX + firstY * segmentY) / lengthSquared)) : 0;
	return Math.sqrt((firstX + projection * segmentX) ** 2 + (firstY + projection * segmentY) ** 2);
};

const getNearestZoneBoundaryDistanceMeters = (deliveryZones: any, longitude: number, latitude: number) => {
	let nearestDistance = Infinity;
	deliveryZones.features.forEach((feature: any) => {
		const geometry = feature.geometry;
		if (!geometry) return;
		const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.type === 'MultiPolygon' ? geometry.coordinates : [];
		polygons.forEach((polygon: number[][][]) => polygon.forEach((ring: number[][]) => {
			for (let i = 0; i < ring.length; i++) {
				const distance = getDistanceToSegmentMeters(longitude, latitude, ring[i], ring[(i + 1) % ring.length]);
				if (distance < nearestDistance) nearestDistance = distance;
			}
		}));
	});
	return nearestDistance;
};

const getZoneDeliveryDays = (feature: any): number[] => {
	const description = feature.properties.description;
	const text = typeof description === 'string' ? description : description && description.value || '';
	const tokens: string[] = (text.replace(/<[^>]*>/g, ' ').match(/[A-Za-z]+/g) || []) as string[];
	const dayIndexes: {[day: string]: number} = {
		sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2,
		wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4,
		fri: 5, friday: 5, sat: 6, saturday: 6,
	};
	const days: number[] = [];
	tokens.forEach(tokenValue => {
		const token = tokenValue.toLowerCase();
		const dayIndex = dayIndexes[token];
		if (dayIndex !== undefined && days.indexOf(dayIndex) === -1) days.push(dayIndex);
	});
	return days;
};

const Cart: React.FunctionComponent<{ onClick?: () => void }> = ({
	onClick,
	children,
}: any) => {

	const [isOpen, setIsOpen] = useState(false);	
	const [locationStatus, setLocationStatus] = useState<'idle' | 'loading' | 'located' | 'fallback' | 'outside' | 'ambiguous'>('idle');
	const [locationMessage, setLocationMessage] = useState('');
	const [showManualZoneInstructions, setShowManualZoneInstructions] = useState(false);
	const [matchedZone, setMatchedZone] = useState<any>(null);
	const [zoneDeliveryDays, setZoneDeliveryDays] = useState<number[]>([]);
	const [isAvailable, setAvailable] = useState(true);
	//const [deliveryDate, setDeliveryDate] = useState( () => (new Date()).getUTCHours() > 5 ? new Date((new Date()).setDate((new Date()).getDate() + 2)) : new Date((new Date()).setDate((new Date()).getDate() + 1)) );	
	const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
	const [isDelivery, setDelivery] = useState(true);	
	const [isGuest, setIsGuest] = useState(false);
	const [isLoggedIn, setLoggedIn] = useState(false);
	const [openCheckout, setOpenCheckout] = useState(false);
	const [note, setNote] = useState('');
	const [deliveryNote, setDeliveryNote] = useState('');	
	const [isDeliveryText, setDeliveryText] = useState(false);
	const [isNextDayDelivery, setNextDayDelivery ] = useState(true);	
	const [errorText, setErrorText] = useState('select shipping date');
	const [disabled, setDisabled] = useState(false)
	
	const { products, totalPrice, currency, clearCart } = useContext(
		LocalCartContext
	);
	const { store, addToCartAndCheckout, createToken, associateCustomer } = useContext(CartContext);
	const { checkout, updating } = store;

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		let isActive = true;
		setLocationStatus('loading');
		setLocationMessage('Requesting your location…');
		setShowManualZoneInstructions(false);
		setMatchedZone(null);
		setZoneDeliveryDays([]);
		setDeliveryDate(undefined);

		const requestLocation = (zonesGeoJson: any) => {
			if (!navigator.geolocation) {
				setLocationStatus('fallback');
				setLocationMessage('Location is not available in this browser. Use the map below to determine your zone, then select a delivery date for that zone.');
				setShowManualZoneInstructions(true);
				return;
			}

			navigator.geolocation.getCurrentPosition(
			(position) => {
				if (isActive) {
					const {longitude, latitude, accuracy} = position.coords;
					const matchingZones = findZonesAtPoint(zonesGeoJson, longitude, latitude);
					const nearestBoundaryDistance = getNearestZoneBoundaryDistanceMeters(zonesGeoJson, longitude, latitude);
					if (!Number.isFinite(accuracy) || accuracy >= nearestBoundaryDistance) {
						setLocationStatus('ambiguous');
						setLocationMessage('Your location is too close to a delivery-zone boundary to identify it reliably. Please use the map and calendar below.');
						setShowManualZoneInstructions(true);
					} else if (matchingZones.length === 0) {
						setLocationStatus('outside');
						setLocationMessage('Sorry, we do not deliver at your location. You can still select a delivery date from the calendar below.');
						setShowManualZoneInstructions(true);
					} else if (matchingZones.length > 1) {
						setLocationStatus('ambiguous');
						setLocationMessage('Your location is near overlapping delivery zones. Please use the map to determine your zone and choose a suitable date from the calendar below.');
						setShowManualZoneInstructions(true);
					} else {
						const days = getZoneDeliveryDays(matchingZones[0]);
						if (!days.length) {
							setLocationStatus('ambiguous');
							setLocationMessage('We found a nearby delivery zone but could not read its delivery days. Please use the map and calendar to choose a suitable date.');
							setShowManualZoneInstructions(true);
						} else {
							setMatchedZone(matchingZones[0]);
							setZoneDeliveryDays(days);
							setLocationStatus('located');
							setLocationMessage(`Your location is in ${matchingZones[0].properties.name}. Select one of the available delivery dates below.`);
						}
					}
				}
			},
			(error) => {
				if (isActive) {
					setLocationStatus('fallback');
					setLocationMessage(error.code === 1
						? 'Location permission was declined. Use the map below to determine your zone, then select a delivery date for that zone.'
						: 'We could not get your location. Use the map below to determine your zone, then select a delivery date for that zone.');
					setShowManualZoneInstructions(true);
				}
			},
			{ enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
		);
		};

		fetch('/delivery-zones.geojson')
			.then(response => {
				if (!response.ok) throw new Error(`Unable to load delivery zones (${response.status})`);
				return response.json();
			})
			.then(zonesGeoJson => {
				if (!isActive) return;
				if (!zonesGeoJson || zonesGeoJson.type !== 'FeatureCollection' || !Array.isArray(zonesGeoJson.features)) {
					throw new Error('Delivery zones file has an invalid GeoJSON structure');
				}
				requestLocation(zonesGeoJson);
			})
			.catch(() => {
				if (!isActive) return;
				setLocationStatus('fallback');
				setLocationMessage('We could not load delivery-zone data. Use the map below to determine your zone, then select a delivery date from the calendar.');
				setShowManualZoneInstructions(true);
			});

		return () => { isActive = false; };
	}, [isOpen]);

	const handleCheckout = async () => {
		//console.log("note: ", note)
		if (!deliveryDate) return;
		
		 addToCartAndCheckout((deliveryDate.getDate() + '/' + (deliveryDate.getMonth() + 1) + '/' + deliveryDate.getFullYear()), note);
		
		clearCart();
		//onClick();
	};
	const getPrice = (price: any) =>
		Intl.NumberFormat(undefined, {
			currency: currency,
			minimumFractionDigits: 2,
			style: 'currency',
		}).format(parseFloat(price ? price : 0));
		

	const checkAuth = async () => {
	//const checkAuth = () => {		
		if (!deliveryDate) {
			setErrorText('Select shipping date');
			return;
		}
		const apiName = 'shop';
		//console.log("note: ", note)
		let dateParm = deliveryDate.toLocaleDateString("en-IN", {timeZone: 'Asia/Bangkok'}).split('/')
		const path = `/getNumOrdersByDate/farmtotruckthailand/${dateParm[2]}-${dateParm[1]}-${dateParm[0]}` ; 
		/*API.get(apiName, path)
		  .then(response => {
			// Add your code here
			//console.log("API resp: ", response)
			if (response.numOrders > 10) {
				//setDelivery(true)
				setAvailable(false)
			}
			else {
				//setDelivery(true)
				setAvailable(true)
			}
			let currdate = new Date();
			const diffTime = Math.abs(deliveryDate.getTime() - currdate.getTime())/ (1000 * 60 * 60 * 24);
			if(response.utchour >= 5 && diffTime <= 1 ) {
				setNextDayDelivery(false)
			}
		  })
		  .catch(error => {
			//console.log(error);
		 });*/
		 setDisabled(true)
		const response = await API.get(apiName, path);
		setDisabled(false)
		if (response.numOrders > 10 && !response.override) {
			//setDelivery(true)
			setAvailable(false)
		}
		/*if (response.numOrders >= 0 && !response.override) { //temp for flood situtaion 
			//setDelivery(true)
			setAvailable(false)
		}*/		
		else {
			//setDelivery(true)
			setAvailable(true)
			let currdate = new Date();
			const diffTime = Math.abs(deliveryDate.getTime() - currdate.getTime())/ (1000 * 60 * 60 * 24);
			//console.log(`difftime: ${diffTime}`)
			if(response.utchour >= 5 && diffTime <= 1 ) {
				setNextDayDelivery(false)
			}
			else {
				setNextDayDelivery(true)
				const user = await Amplify.Auth.currentUserInfo();
				setOpenCheckout(true);
				if (user) {
					setLoggedIn(true)
				}			
			}			
		}
	 
		/*Amplify.Auth.currentUserInfo()
			.then(user => setLoggedIn(true))
		  .catch(error => {
			console.log(error);
		 })		*/	
	}

	const checkTime = async (selectDate: Date) => {
		/*let myHeaders = new Headers(); 
		myHeaders.append("accept", `application/json`);

		let requestOptions = {
		  method: 'GET',
		  headers: myHeaders,	  
		  redirect: 'follow',
		};
		let response = await fetch(`https://timeapi.io/api/Time/current/zone?timeZone=Asia/Bangkok`, requestOptions);
		let responseJson = await response.json();		
		let hour = parseInt(responseJson.time.split(':')[0])
		let compareDate = new Date(responseJson.date)
		compareDate.setDate(compareDate.getDate() + 1);*/
		let hour = 11;
		//if (hour >= 11 && (compareDate.getTime() <= selectDate.getTime())) {
		if (hour >= 11) {
		 	setErrorText('Next day shipping available only till 12 noon. Select next available shipping date')
		 	setDeliveryDate(selectDate)						
			//setNextDayDelivery(false)
		}
		else {
			setErrorText('')
		 	setDeliveryDate(selectDate)
		}
	}	
	
	useEffect(() => {
		if (!deliveryDate) {
			setErrorText('Select shipping date')
			return
		}
		if (!isDelivery) {
			setErrorText('Sorry! - we do not deliver on Saturdays, Sundays and Mondays - please choose another day')
			return
		}
		if (!isNextDayDelivery) {
			setErrorText('Next day shipping is available till 12 noon. Please select a later date')
			return
		}	
		if (!isAvailable) {
			setErrorText('Sorry! - no available Delivery capacity - please choose another date')
			//setErrorText('Due to flood situation we are unable to take orders between 8th - 11th Oct. Sorry for the inconvenience') //temp for flood situation		
			return
		}						
		if (! /\S/.test(deliveryNote)) {
			//setDeliveryText(true)
		 	setErrorText('Please type unavailable delivery time or dash(-) for none')
		 	return
		}
			
		setErrorText('')			
/*! isDelivery && ? 
					(<Alert>Sorry! - no available Delivery capacity - please choose another date</Alert>) : 
					! isDeliveryText ?
					(<Alert>Please type unavailable delivery time or dash(-) for none</Alert>) :	
					! isNextDayDelivery ?	
					(<Alert>Next day shipping is available till 12 noon. Please selet a later date</Alert>) :									
					(<Alert>Sorry! - we do not deliver on Saturdays, Sundays and Mondays - please choose another day</Alert>) 					
					*/				
	}, [deliveryNote, deliveryDate, isAvailable, isDelivery, isNextDayDelivery]);	
		 
	useEffect(() => {
		if(deliveryDate) {
			if( ((deliveryDate.getDay() === 0) || (deliveryDate.getDay() === 1) || (deliveryDate.getDay() === 6)) && !inclusionDates(deliveryDate)) { //do not deliver on Sat Sun Mon
			//if( ((deliveryDate.getDay() === 0) || (deliveryDate.getDay() === 6)) && !inclusionDates(deliveryDate)) { 			
			//if((deliveryDate.getDay() === 0) ) { //change deliver on thu now
				//setAvailable(false)
				setDelivery(false)
				//setErrorText('Delivery day')
			}
			else {
				setDelivery(true)
				setAvailable(true)
				setNextDayDelivery(true)
			}
			//temp for 04102021 remove after
			/*else if(deliveryDate.getDay() === 1) {
				setDelivery(true)
				setAvailable(false)		
			}*/
			//temp for 13092021 remove after
			/*else {
				
				const apiName = 'shop';
				//console.log("note: ", note)
				const path = '/getNumOrdersByDate/farmtotruckthailand/' + deliveryDate.toISOString().split('T')[0]; 
				API.get(apiName, path)
				  .then(response => {
					// Add your code here
					//console.log("API resp: ", response)
					if (response.numOrders > 10) {
						setDelivery(true)
						setAvailable(false)
					}
					else {
						setDelivery(true)
						setAvailable(true)
					}
				  })
				  .catch(error => {
					//console.log(error);
				 });
			}*/
		}	
	}, [deliveryDate]);		

	  const isTueToFri = (date: Date) => {
		const day = date.getDay();
		return (day !== 0 && day !== 1 && day !== 6) || inclusionDates(date);
		//return (day !== 0 && day !== 6) || inclusionDates(date); 
	  };

	  const inclusionDates = (date: Date) => {
		const day = date.getDate();
		const month = date.getMonth(); // November is 10 (0-based)
		const year = date.getFullYear();
		return year === 2025 && month === 10 && (day === 8 || day === 9 || day === 10);
	  };
	
	const endDate = new Date();
	endDate.setDate(endDate.getDate() + 21);
	const startDate = new Date();
	startDate.setDate(startDate.getDate() + (startDate.getUTCHours() > 5 ? 2 : 1));
	const zoneDateOptions: Date[] = [];
	if (matchedZone && zoneDeliveryDays.length) {
		const candidateDate = new Date(startDate);
		while (candidateDate <= endDate && zoneDateOptions.length < 3) {
			const isHoliday = holidayDates.some(holiday => holiday.toDateString() === candidateDate.toDateString());
			if (zoneDeliveryDays.indexOf(candidateDate.getDay()) !== -1 && !isHoliday) {
				zoneDateOptions.push(new Date(candidateDate));
			}
			candidateDate.setDate(candidateDate.getDate() + 1);
		}
	}
	
	if (openCheckout) {
		//console.log(note)
		let comboNote = note 
		comboNote = comboNote + 
`
unavailable timeslots:
` + deliveryNote;
		
		//console.log(comboNote)

		return isGuest ? (<WebCheckout onClick={onClick} setLoggedIn={setLoggedIn} deliveryDate={deliveryDate} note={comboNote}/> ) : 
		(

			<Box sx={styles.wrapper}>
			<AuthStateApp>
				<WebCheckout onClick={onClick} setLoggedIn={setLoggedIn} deliveryDate={deliveryDate} note={comboNote}/>
			</AuthStateApp>	
			{!isLoggedIn && (<Box sx={styles.buttonStyle}>		
				<Button onClick={() => {setIsGuest(!isGuest);}}>
					{updating ? 'Loading ...' : 'Continue as Guest'}
				</Button>
			</Box>)}
			</Box>			
		);	
	}
	else if (isOpen) {
		return (

				<Box id="cart" sx={styles.wrapper}>
		
					<Heading as="h2" sx={styles.title}>
						Shipping Date 
						<Button title="Close" variant="text" onClick={() => {setIsOpen(false); onClick()}}>
							<IoIosClose />
						</Button>
					</Heading>
					<Box style={{flexGrow: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch'}}>
				  <Box sx={{marginX: 'auto', width: '100%', maxWidth: 750, py: [10, 15, 30]}}>
					  <Heading as="h3" sx={{mb: 2}}>New Delivery Zones - click your zone for shipping days</Heading>
					  <Text sx={{fontSize: 2, color: 'gray', mb: 1}}>10% discount on pickup</Text>
					  <Text sx={{fontSize: 1, color: 'gray', mb: 2}}>click on red cart icon in map for details</Text>
					  <Text sx={{fontSize: 1, color: 'gray', mb: 3}}>pickup time 10 am - 3 pm - reconform before visiting</Text>
					  {locationStatus === 'outside' ? (
						<Alert role="alert" sx={{mb: 2}}>Sorry, we do not deliver at your location. You can still select a delivery date from the calendar below.</Alert>
					  ) : (
						<Text role="status" aria-live="polite" sx={{fontSize: 1, color: 'gray', mb: 2}}>{locationMessage}</Text>
					  )}
					  {locationStatus === 'located' && (
						<Button type="button" variant="text" sx={{mb: 2, px: 0}} onClick={() => {
							setShowManualZoneInstructions(true);
							setLocationMessage('Use the map below to verify your zone, then choose a date that matches its delivery days.');
						}}>
							My location result looks incorrect — use the map manually
						</Button>
					  )}
					  {showManualZoneInstructions && (
						<Box sx={{mb: 3}}>
							<Text sx={{fontSize: 1, color: 'gray', mt: 1}}>
								Find your area on the map, click its zone for delivery details, then choose a shipping date that matches that zone. The selected date is used for your order.
							</Text>
						</Box>
					  )}
					  <iframe 
					    src="https://www.google.com/maps/d/u/2/embed?mid=1hXUzkfZLqeW3kdb2eBNqoDVA9Mo0JwU&ehbc=2E312F" 
					    width="100%" 
					    height="480" 
					    style={{border: 0}}
					  />
				  </Box>
				  <Box sx={{marginX: 'auto', width: '100%', maxWidth: 750, py: [10, 15, 30]}}>
					  {locationStatus === 'located' && matchedZone && zoneDateOptions.length > 0 && !showManualZoneInstructions ? (
						<Box>
							<Label>Select one delivery date for {matchedZone.properties.name}: <span sx={{color: 'red'}}>*</span></Label>
							<Grid gap={2} columns={[1, 1, 3]} sx={{mt: 2}}>
								{zoneDateOptions.map(date => {
									const isSelected = deliveryDate && deliveryDate.toDateString() === date.toDateString();
									return (
										<Button
											key={date.toISOString()}
											type="button"
											aria-pressed={!!isSelected}
											onClick={() => setDeliveryDate(date)}
											sx={{
												minHeight: 76,
												display: 'flex',
												flexDirection: 'column',
												justifyContent: 'center',
												backgroundColor: isSelected ? '#292929' : 'white',
												color: isSelected ? 'white' : '#292929',
												border: '1px solid #292929',
												'&:hover': {backgroundColor: isSelected ? '#292929' : '#f5f5f5'},
											}}
										>
											<Text>{date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</Text>
											<Text>{date.toLocaleDateString(undefined, {weekday: 'long'})}</Text>
										</Button>
									);
								})}
							</Grid>
						</Box>
					  ) : (
						<Box>
							<Label>Pick a shipping date according to the zone map above: <span sx={{color: 'red'}}>*</span></Label>
							<Label>(Other provinces take 2-3 days.)</Label>
							<DatePicker
								selected={deliveryDate}
								excludeDates={holidayDates}
								minDate={startDate}
								maxDate={endDate}
								filterDate={isTueToFri}
								onChange={(selectDate: Date | null) => { if (selectDate) setDeliveryDate(selectDate)}}
								placeholderText={"shipping date"}
							/>
						</Box>
					  )}
				  </Box>
				  <Box sx={{marginX: 'auto', width: '100%', maxWidth: 750, py: [10, 15, 30]}}>
					  <Label><span sx={{fontWeight: 'bold', color: 'red'}}>INCONVENIENT&nbsp;</span> delivery hours: <span sx={{color: 'red'}}>*</span></Label>
                  	  <Textarea placeholder="During what time that you CANNOT receive the package delivery. E.g. Before 6:30 AM and after 10PM / Dash(-) for NONE" rows={3} onChange={event => { setDeliveryNote(event.target.value)}}/>
                      <Text sx={{fontSize: 1, color: 'gray', mt: 1}}>
                        Regardless of your inconvenient time, please note that the delivery usually happens between 8:00-17:00.
                      </Text>
				  </Box>
				  
				  <Box sx={{marginX: 'auto', width: '100%', maxWidth: 750, py: [10, 15, 30]}}>
				  	<Textarea placeholder="Special request notes:" rows={8} onChange={event => { setNote(event.target.value)}}/>
				  </Box>
				  <Box sx={styles.buttonStyle}>
					{isAvailable  && !errorText ? 
					(<Button onClick={() => checkAuth()}>
						{disabled ? 'Loading ...' : 'checkout'}
					</Button>) :
					<Alert>{errorText}</Alert>
					} 
					{ /*! isDelivery && ? 
					(<Alert>Sorry! - no available Delivery capacity - please choose another date</Alert>) : 
					! isDeliveryText ?
					(<Alert>Please type unavailable delivery time or dash(-) for none</Alert>) :	
					! isNextDayDelivery ?	
					(<Alert>Next day shipping is available till 12 noon. Please selet a later date</Alert>) :									
					(<Alert>Sorry! - we do not deliver on Saturdays, Sundays and Mondays - please choose another day</Alert>) 					
					*/} 
				  </Box>
				  <Box sx={{marginX: 'auto', width: '100%', maxWidth: 750, py: [10, 15, 30]}}>
					  <Label><span sx={{color: 'red'}}>* </span> Required inputs</Label>
				  </Box>
				  </Box>
			</Box>	

		);	
	}
	else {
		return (
			<Box sx={styles.wrapper}>
				<Heading as="h2" sx={styles.title}>
					Cart
					<Button title="Close" variant="text" onClick={() => {setIsOpen(false); onClick()}}>
						<IoIosClose />
					</Button>
				</Heading>

				<Flex sx={styles.totalPrice}>
					<Box>
						<Text>Total Amount:</Text>
						<Heading as="h4">{getPrice(totalPrice)}</Heading>
					</Box>
				</Flex>

				<Box sx={styles.cartItems}>{children}</Box>

				<Box sx={styles.buttonStyle}>
					{products.length === 0 ? (
						<Button onClick={onClick}>
							<svg width="15.394" height="10" viewBox="0 0 15.394 10">
								<path
									id="arrow-left"
									d="M8.3,6.343,9.551,7.6,6.66,10.473l12.025.012,0,1.768L6.691,12.241,9.533,15.1,8.28,16.343,3.292,11.332Z"
									transform="translate(-3.292 -6.343)"
									fill="#fff"
								/>
							</svg>
							Back to shop
						</Button>
					) : (
						<Button onClick={() => {setIsOpen(!isOpen);}}>
							{updating ? 'Loading ...' : 'Proceed to checkout'}
						</Button>
					)}
				</Box>
			</Box>
		);	
	}

};

export default Cart;
